import { FlyToInterpolator, type MapViewState, WebMercatorViewport } from "@deck.gl/core";
import { IconLayer, PolygonLayer, ScatterplotLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { Device, DeviceStatusInfo } from "@fairfleet/geotab";
import LabelIcon from "@mui/icons-material/Label";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import useTheme from "@mui/material/styles/useTheme";
import Tooltip from "@mui/material/Tooltip";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Map, { Layer, Source } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useAssetLabels } from "../hooks/useAssetLabels";
import {
  type ClusterOrPoint,
  type ViewportBounds,
  isCluster,
  useCluster,
} from "../hooks/useCluster";
import { type DeviceLocation, useDeviceLocation } from "../hooks/useDeviceLocation";
import { useIncrementalFetch } from "../hooks/useIncrementalFetch";
import { useMapAssetRows } from "../hooks/useMapAssetRows";
import { useMapGroupStyles } from "../hooks/useMapGroupStyles";
import { useMapSettings } from "../hooks/useMapSettings";
import {
  AssetLabelBadge,
  POWERTRAIN_LABELS,
} from "../models/assetLabelBadge";
import { boundsOfPositions, viewportForBounds } from "../models/fitBounds";
import { DEFAULT_MAP_VIEWPORT, groupStyleOf, hexToRgba } from "../models/mapSettings";
import { MAP_STYLES } from "../models/mapStyles";
import { HAS_TRAFFIC_PROXY, TRAFFIC_LAYER_KEYS, trafficTileUrl } from "../models/trafficNrw";
import {
  capCache,
  getHeadingMarker,
  getVehicleIcons,
  groupSymbolIcon,
  HEADING_MARKER_SIZE_PX,
  svgToDataURL,
} from "../models/vehicleIcons";
import { ZoneOutline } from "../models/zoneContainment";
import { AssetLabelOverlay } from "./AssetLabelOverlay";
import { MapControlStack } from "./MapControlStack";
import MapSearchBar from "./MapSearchBar";
import { MapSettingsPanel } from "./MapSettingsPanel";
import { useAnimatedPositions } from "./useAnimatedPositions";
import { vehicleIconAngle } from "./vehicleIconAngle";
import VehicleInfoPanel from "./VehicleInfoPanel";
import { VehicleListPanel } from "./VehicleListPanel";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { MAP_DIAGNOSTICS } from "@/constants/constants";
import { RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useFuelCellDeviceIds } from "@/hooks/devices/useFuelCellDeviceIds";
import useLastKnownPositions from "@/hooks/positions/useLastKnownPositions";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useZoneOutlines } from "@/hooks/zone/useZoneOutlines";
import { clamp } from "@/utilities/clamp";


// ─── Cluster icon ──────────────────────────────────────────────────

/** Keyed by colour as well as count: the same number in two colours is two icons. */
const clusterIconCache = new globalThis.Map<string, string>();
function getClusterIcon(count: number, color: string): string {
  const cacheKey = `${color}::${count}`;
  const cached = clusterIconCache.get(cacheKey);
  if (cached) return cached;

  const size = Math.min(128, 48 + Math.log2(count) * 12);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">
    <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" opacity="0.85" stroke="#fff" stroke-width="2"/>
    <text x="${size / 2}" y="${size / 2}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="${Math.min(size * 0.4, 24)}" font-family="Roboto, sans-serif" font-weight="500">${count}</text>
  </svg>`;
  const url = svgToDataURL(svg);
  clusterIconCache.set(cacheKey, url);
  capCache(clusterIconCache);
  return url;
}

/**
 * How big a cluster bubble is drawn, in screen pixels, for the number of vehicles in it.
 *
 * Pixels, not a size on the ground. Scaled with the zoom — which is what `sizeUnits: "common"`
 * does — an icon grows and shrinks as the map is zoomed and, clamped at both ends, changes size
 * at two arbitrary zoom levels and nowhere else. It also lets the name layout keep names off
 * these bubbles, which it can only do if the size it is told is the size that gets drawn.
 */
function clusterIconSize(count: number): number {
  return Math.min(60, 30 + Math.log2(count) * 5);
}

// ─── Types & constants ─────────────────────────────────────────────

interface ViewState extends MapViewState {
  pitch: number;
  bearing: number;
}

/**
 * Parameter shape deck.gl instantiates `onViewStateChange` with. The prop is a
 * generic callback, so the concrete type has to be read back off `DeckGL`.
 */
type ViewStateChange = Parameters<
  NonNullable<ComponentProps<typeof DeckGL>["onViewStateChange"]>
>[0];

type Coord = [number, number, string, boolean];

/**
 * Empty stand-ins while positions and statuses load, one object each for the life of the page.
 *
 * A `= []` default is a new array on every render, and effects downstream set state whenever
 * their input changes: each render then caused the next, and the built app stopped with React
 * error #185 ("Maximum update depth exceeded") whenever loading took a moment.
 */
const NO_POSITIONS: NonNullable<ReturnType<typeof useLastKnownPositions>["data"]> = [];
const NO_STATUS_INFOS: NonNullable<ReturnType<typeof useStatusInfosByIds>["data"]> = {};

const INITIAL_VIEW_STATE: ViewState = DEFAULT_MAP_VIEWPORT;

const BOUNDS_SETTLE_MS = 75;
const USE_BOUNDED_POSITION_FETCH = false;
const POSITION_BOUNDS_ZOOM_THRESHOLD = 14;
const POSITION_BOUNDS_PADDING_FACTOR = 2.0;
const POSITION_REFETCH_MS = 12000;
const STATUS_INFO_REFETCH_MS = 2000;
/** Above this many vehicles drawn on their own, their status is refreshed with the rest. */
const FAST_STATUS_MAX_VEHICLES = 100;
const SLOW_STATUS_MAX_AGE_MS = 60_000;
const STATUS_PAGE_SIZE = 500;
const DEVICE_PAGE_SIZE = 250;
/** Names and groups hardly change while the map is open; a rename shows up after this. */
const DEVICE_NAMES_MAX_AGE_MS = 30 * 60_000;
const NO_IDS: string[] = [];
const POSITION_ANIMATION_FALLBACK_MS = Math.round(POSITION_REFETCH_MS * 0.85);
const POSITION_RETENTION_MS = 30000;
const MIN_MAP_LAT = -85;
const MAX_MAP_LAT = 85;
const FOLLOW_POSITION_LERP_FACTOR = 0.16;
const FOLLOW_POSITION_EPSILON = 0.000005;
const DRIVING_STATE_SWITCH_DELAY_MS = 1200;
/** How long the map has to sit still before where it is looking is worth storing. */
const VIEWPORT_PERSIST_DELAY_MS = 1000;
/** Groups come along because a vehicle can be coloured by the group it is in. */
// Plate and VIN are only here for the search bar, which searches these instead of asking Geotab.
const DEVICE_FIELDS = ["id", "name", "serialNumber", "groups", "licensePlate", "vehicleIdentificationNumber"];

/** The positions out of a set of coordinates, by device id. */
function toPositionMap(coords: Coord[]): globalThis.Map<string, [number, number]> {
  const positions = new globalThis.Map<string, [number, number]>();
  for (const [lng, lat, id] of coords) {
    positions.set(id, [lng, lat]);
  }
  return positions;
}

function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function normalizeBearing(bearing: number): number {
  return ((bearing % 360) + 360) % 360;
}

// ─── Viewport bounds computation ──────────────────────────────────

function computeViewportBounds(
  vs: ViewState,
  width: number,
  height: number,
): ViewportBounds | null {
  if (width <= 0 || height <= 0) return null;
  const vp = new WebMercatorViewport({
    longitude: vs.longitude,
    latitude: vs.latitude,
    zoom: vs.zoom,
    pitch: vs.pitch,
    bearing: vs.bearing,
    width,
    height,
  });
  const [west, south, east, north] = vp.getBounds();
  return { west, south, east, north };
}

// ─── Fixed tooltip component ──────────────────────────────────────

interface TooltipInfo {
  x: number;
  y: number;
  isCluster: boolean;
  clusterCount?: number;
  deviceId?: string;
}

function FixedTooltip({ info, text }: { info: TooltipInfo | null; text: string | null }) {
  if (!info) return null;
  const isLoading = !info.isCluster && text === null;

  return (
    <div
      style={{
        position: "absolute",
        left: info.x,
        top: info.y - 36,
        pointerEvents: "none",
        zIndex: 1000,
        transform: "translateX(-50%)",
        background: "rgba(0,0,0,0.8)",
        color: "#fff",
        borderRadius: 4,
        padding: "4px 8px",
        fontSize: 14,
        whiteSpace: "nowrap",
        maxWidth: 300,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {isLoading ? (
        <Skeleton variant="text" width={80} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
      ) : (
        text
      )}
    </div>
  );
}

/**
 * A round map button that is either on or off.
 */
function MapToggleButton({
  label,
  active,
  icon,
  onClick,
}: {
  label: string;
  /** Left out for a button that does something rather than switching something. */
  active?: boolean;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <Tooltip title={label} placement="left">
      <Paper elevation={2} sx={{ borderRadius: "50%", overflow: "hidden" }}>
        <IconButton
          onClick={onClick}
          size="medium"
          // The tooltip puts its label on the Paper, not on the button, so without this a screen
          // reader announces nothing but "button".
          aria-label={label}
          aria-pressed={active}
          sx={{ width: 40, height: 40 }}
        >
          {icon}
        </IconButton>
      </Paper>
    </Tooltip>
  );
}

// ─── Component ─────────────────────────────────────────────────────

const LiveMapView = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { t } = useTranslation();

  // Read first: the map is drawn from these, and where it starts looking comes out of them.
  const { settings, update: updateSettings, isFetched: settingsFetched } = useMapSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const groupStyles = useMapGroupStyles();
  const zoneOutlines = useZoneOutlines(settings.showZones);

  /**
   * The traffic overlays actually drawn.
   *
   * A stored choice is kept even where there is no proxy to fetch it from, so moving between
   * deployments does not silently throw it away; it just cannot be drawn here.
   */
  const trafficLayers = useMemo(
    () =>
      HAS_TRAFFIC_PROXY && settings.showTraffic
        ? // The array too, not just the address: a new one each render has MapLibre take the
          // source down and put it back up again.
          TRAFFIC_LAYER_KEYS.map(key => ({ key, tiles: [trafficTileUrl(key)] }))
        : [],
    [settings.showTraffic],
  );

  const themeColors = useMemo(
    () => ({
      icon: primaryColor,
      labelText: theme.palette.text.primary,
      labelBackground: theme.palette.background.paper,
    }),
    [primaryColor, theme.palette.background.paper, theme.palette.text.primary],
  );

  // ─── Container size ──────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: Math.round(entry.contentRect.width),
          height: Math.round(entry.contentRect.height),
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ─── View state (controlled for fly-to support) ──────────────────
  const [viewState, setViewState] = useState<ViewState>(INITIAL_VIEW_STATE);

  /**
   * Puts the map back where the reader left it.
   *
   * Once only, and only after the stored settings have actually arrived — otherwise the defaults
   * would count as "the stored viewport" and the map would jump to the middle of Germany a moment
   * after the reader had already started panning.
   */
  const restoredViewportRef = useRef(false);
  useEffect(() => {
    if (restoredViewportRef.current || !settingsFetched) return;
    restoredViewportRef.current = true;

    const stored = settings.viewport;
    if (stored) {
      // A view stored while the map could still turn comes back facing north.
      setViewState(current => ({ ...current, ...stored, bearing: 0 }));
    }
  }, [settingsFetched, settings.viewport]);

  /**
   * Remembers where the map is looking, a moment after it stops moving.
   *
   * Not on every frame of a pan: each stored record is a round trip, and the settings store
   * already waits before writing. This waits as well so a drag becomes one write, not eighty.
   */
  useEffect(() => {
    if (!restoredViewportRef.current) return;

    const timeout = setTimeout(() => {
      updateSettings({
        viewport: {
          longitude: viewState.longitude,
          latitude: viewState.latitude,
          zoom: viewState.zoom,
          pitch: viewState.pitch,
          bearing: viewState.bearing,
        },
      });
    }, VIEWPORT_PERSIST_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [viewState, updateSettings]);

  const flyTo = useCallback(
    (longitude: number, latitude: number, zoom = 15, transitionDuration = 2000) => {
      setViewState(prev => ({
        ...prev,
        longitude,
        latitude,
        zoom,
        bearing: 0,
        transitionDuration,
        transitionInterpolator: new FlyToInterpolator(),
      }));
    },
    [],
  );

  // ─── Viewport bounds ───────────────────────────────────────────
  const boundsRef = useRef<ViewportBounds | null>(null);
  const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stableBounds, setStableBounds] = useState<ViewportBounds | null>(null);

  useEffect(() => {
    const newBounds = computeViewportBounds(viewState, containerSize.width, containerSize.height);
    if (!newBounds) return;

    boundsRef.current = newBounds;

    if (!stableBounds) {
      setStableBounds(newBounds);
      return;
    }

    if (boundsTimerRef.current) clearTimeout(boundsTimerRef.current);
    boundsTimerRef.current = setTimeout(() => {
      setStableBounds(newBounds);
    }, BOUNDS_SETTLE_MS);
  }, [viewState, containerSize.width, containerSize.height]); // eslint-disable-line react-hooks/exhaustive-deps

  const positionBounds = useMemo(() => {
    if (!USE_BOUNDED_POSITION_FETCH) return undefined;
    if (!stableBounds) return undefined;

    if (viewState.zoom < POSITION_BOUNDS_ZOOM_THRESHOLD) return undefined;

    const spanLng = stableBounds.east - stableBounds.west;
    const spanLat = stableBounds.north - stableBounds.south;
    // 200% padding: load positions in a 5× viewport area in each direction.
    // This ensures devices that were in a cluster near the edge are still
    // present in rawCoords when the cluster breaks apart at the next zoom step.
    const padLng = spanLng * POSITION_BOUNDS_PADDING_FACTOR;
    const padLat = spanLat * POSITION_BOUNDS_PADDING_FACTOR;

    const left = Math.max(-180, stableBounds.west - padLng);
    const right = Math.min(180, stableBounds.east + padLng);
    const bottom = Math.max(-85, stableBounds.south - padLat);
    const top = Math.min(85, stableBounds.north + padLat);

    return {
      bounds: { left, bottom, right, top },
    };
  }, [stableBounds, viewState.zoom]);

  const { data: rawCoords = NO_POSITIONS } = useLastKnownPositions(positionBounds, {
    refetchIntervalMs: POSITION_REFETCH_MS,
  });

  const retainedCoordsRef = useRef<
    globalThis.Map<string, { lng: number; lat: number; isDriving: boolean; seenAt: number }>
  >(new globalThis.Map());
  const [stableCoords, setStableCoords] = useState<Coord[]>([]);

  useEffect(() => {
    const now = Date.now();
    const retained = retainedCoordsRef.current;

    for (const [deviceId, entry] of retained.entries()) {
      if (now - entry.seenAt > POSITION_RETENTION_MS) {
        retained.delete(deviceId);
      }
    }

    for (const [lng, lat, id, isDriving] of rawCoords) {
      if (!id || !Number.isFinite(lng) || !Number.isFinite(lat)) continue;
      retained.set(id, {
        lng: normalizeLng(lng),
        lat: clamp(lat, MIN_MAP_LAT, MAX_MAP_LAT),
        isDriving: !!isDriving,
        seenAt: now,
      });
    }

    const merged = Array.from(retained.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([deviceId, entry]) => [entry.lng, entry.lat, deviceId, entry.isDriving] as Coord);
    setStableCoords(merged);
  }, [rawCoords]);

  /**
   * The colour of the cluster bubbles.
   *
   * A bubble stands for several vehicles, so it can only carry a group's colour when exactly one
   * group is set up; with two, it would be claiming one group's colour for vehicles of both.
   */
  const clusterColor =
    (groupStyles.groups.length === 1 ? groupStyles.groups[0].color : "") ||
    settings.vehicleIconColor ||
    primaryColor;

  // ─── Selection & follow ─────────────────────────────────────────
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const followingDeviceRef = useRef<string | null>(null);

  const handleDeviceLocated = useCallback(
    ({ longitude, latitude }: DeviceLocation) => {
      followingDeviceRef.current = null;
      flyTo(longitude, latitude, Math.max(viewState.zoom, 15), 1200);
    },
    [flyTo, viewState.zoom],
  );
  const {
    location: deviceLocation,
    isLocating,
    locate,
    clear: clearDeviceLocation,
  } = useDeviceLocation(handleDeviceLocated);

  // ─── Clustering ─────────────────────────────────────────────────
  const { clusters, getClusterExpansionZoom } = useCluster(stableCoords, viewState.zoom, {
    enabled: settings.clusteringEnabled,
    radius: settings.clusterRadiusPx,
  });

  // ─── Data fetching ───────────────────────────────────────────────
  const visibleStatusIds = useRef<string[]>([]);
  /**
   * Every vehicle inside the current view, whether the map draws it on its own or folded into a
   * cluster bubble. The vehicle list shows all of them, so all of them are asked about — the
   * owner chose that over a list that empties itself as soon as the map is zoomed out.
   */
  const stableVisibleIds = useMemo(() => {
    const visible = new Set<string>();
    for (const [longitude, latitude, deviceId] of stableCoords) {
      if (
        !stableBounds ||
        (longitude >= stableBounds.west &&
          longitude <= stableBounds.east &&
          latitude >= stableBounds.south &&
          latitude <= stableBounds.north)
      ) {
        visible.add(deviceId);
      }
    }
    const sorted = Array.from(visible).sort();
    const key = sorted.join(",");
    const prevKey = visibleStatusIds.current.join(",");
    if (key === prevKey) return visibleStatusIds.current;
    visibleStatusIds.current = sorted;
    return sorted;
  }, [stableCoords, stableBounds]);
  const drawnStatusIds = useRef<string[]>([]);
  /** The vehicles drawn on their own inside the view: these carry a name, flags and a heading. */
  const drawnOnScreenIds = useMemo(() => {
    const drawn: string[] = [];
    for (const point of clusters) {
      if (isCluster(point)) continue;
      const [longitude, latitude] = point.geometry.coordinates;
      if (
        !stableBounds ||
        (longitude >= stableBounds.west &&
          longitude <= stableBounds.east &&
          latitude >= stableBounds.south &&
          latitude <= stableBounds.north)
      ) {
        drawn.push(point.properties.deviceId);
      }
    }
    drawn.sort();
    if (drawn.join(",") === drawnStatusIds.current.join(",")) return drawnStatusIds.current;
    drawnStatusIds.current = drawn;
    return drawn;
  }, [clusters, stableBounds]);

  const allDeviceIds = useMemo(() => stableCoords.map(([, , deviceId]) => deviceId), [stableCoords]);
  const nameFirstIds = useMemo(
    () => [...drawnOnScreenIds, ...stableVisibleIds],
    [drawnOnScreenIds, stableVisibleIds],
  );

  const { geotabClient, activeSessionId } = useGeotabClient();

  /**
   * Status of the vehicles drawn on their own, polled fast so headings and flags keep up. Only while
   * few are drawn: at a whole-fleet zoom a fast poll of hundreds of vehicles was ~300 KB every few
   * seconds, and there the minute-by-minute refresh below is enough.
   */
  const fastStatusIds = drawnOnScreenIds.length <= FAST_STATUS_MAX_VEHICLES ? drawnOnScreenIds : NO_IDS;
  const { data: fastStatusInfos = NO_STATUS_INFOS, dataUpdatedAt: fastStatusUpdatedAt } = useStatusInfosByIds(
    fastStatusIds,
    MAP_DIAGNOSTICS,
    STATUS_INFO_REFETCH_MS,
  );

  // Every vehicle in view, for the list: each one refreshed once a minute, new ones right away,
  // and a pan that only shifts the view asks for nobody again.
  const fetchStatusPage = useCallback(
    async (ids: string[], signal: AbortSignal) => {
      const infos = await geotabClient.get(
        "DeviceStatusInfo",
        { deviceSearch: { deviceIds: ids }, diagnostics: MAP_DIAGNOSTICS.map(id => ({ id })) },
        ids.length,
        signal,
      );
      return new globalThis.Map(
        (infos ?? []).flatMap(info => (info.device?.id ? [[info.device.id, info] as const] : [])),
      );
    },
    [geotabClient],
  );
  const { data: slowStatusInfos, updatedAt: slowStatusUpdatedAt } = useIncrementalFetch<DeviceStatusInfo>({
    ids: stableVisibleIds,
    priorityIds: drawnOnScreenIds,
    maxAgeMs: SLOW_STATUS_MAX_AGE_MS,
    pageSize: STATUS_PAGE_SIZE,
    fetchPage: fetchStatusPage,
    resetKey: activeSessionId ?? "",
  });

  const statusInfoMap = useMemo(() => {
    if (slowStatusInfos.size === 0) return fastStatusInfos;
    return { ...Object.fromEntries(slowStatusInfos), ...fastStatusInfos };
  }, [slowStatusInfos, fastStatusInfos]);
  const statusInfoMapUpdatedAt = Math.max(fastStatusUpdatedAt, slowStatusUpdatedAt);

  // Names and groups of every vehicle with a position: the ones drawn on screen first, then the
  // rest of the view, then the whole fleet, a page at a time. Kept once fetched, so panning and
  // zooming ask for nothing again.
  const fetchDevicePage = useCallback(
    async (ids: string[], signal: AbortSignal) => {
      const devices = (await geotabClient.call(
        "Get",
        {
          typeName: "Device",
          search: { deviceIds: ids },
          propertySelector: { fields: DEVICE_FIELDS, isIncluded: true },
          resultsLimit: RESULTS_LIMIT_DEVICE,
        },
        signal,
      )) as Device[];
      return new globalThis.Map(devices.map(device => [device.id, device] as const));
    },
    [geotabClient],
  );
  const { data: devicesById } = useIncrementalFetch<Device>({
    ids: allDeviceIds,
    priorityIds: nameFirstIds,
    maxAgeMs: DEVICE_NAMES_MAX_AGE_MS,
    pageSize: DEVICE_PAGE_SIZE,
    fetchPage: fetchDevicePage,
    resetKey: activeSessionId ?? "",
  });

  const groupIdsByDevice = useMemo(() => {
    const groupIds = new globalThis.Map<string, string[]>();
    for (const device of devicesById.values()) {
      groupIds.set(
        device.id,
        (device.groups ?? []).flatMap(group => (group.id ? [group.id] : [])),
      );
    }
    return groupIds;
  }, [devicesById]);

  const deviceNamesById = useMemo(() => {
    const names: Record<string, string | undefined> = {};
    for (const device of devicesById.values()) {
      names[device.id] = device.name;
    }
    return names;
  }, [devicesById]);

  const lastBearingByDeviceRef = useRef(new globalThis.Map<string, number>());

  useEffect(() => {
    for (const [deviceId, info] of Object.entries(statusInfoMap)) {
      if (typeof info?.bearing === "number" && Number.isFinite(info.bearing)) {
        lastBearingByDeviceRef.current.set(deviceId, normalizeBearing(info.bearing));
      }
    }
  }, [statusInfoMap]);

  const speedByDeviceId = useMemo(() => {
    const speeds: Record<string, number | undefined> = {};
    for (const [deviceId, info] of Object.entries(statusInfoMap)) {
      speeds[deviceId] = typeof info?.speed === "number" ? info.speed : undefined;
    }
    return speeds;
  }, [statusInfoMap]);

  const animatedCoords = useAnimatedPositions(stableCoords, {
    fallbackDurationMs: POSITION_ANIMATION_FALLBACK_MS,
    minDurationMs: 1200,
    maxDurationMs: 45000,
    speedByDeviceId,
  });

  const animatedPositionMap = useMemo(() => toPositionMap(animatedCoords), [animatedCoords]);

  // For icon state, prefer fast status polling and fall back to last known position flags.
  const isDrivingMap = useMemo(() => {
    const m: Record<string, boolean> = {};
    for (const [, , id, driving] of stableCoords) {
      m[id] = driving;
    }
    for (const [id, info] of Object.entries(statusInfoMap)) {
      if (typeof info?.isDriving === "boolean") {
        m[id] = info.isDriving;
      }
    }
    return m;
  }, [stableCoords, statusInfoMap]);

  const drivingDisplayStateRef = useRef<
    globalThis.Map<string, { value: boolean; pendingValue?: boolean; pendingSince?: number }>
  >(new globalThis.Map());
  const [displayDrivingMap, setDisplayDrivingMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const now = Date.now();
    const displayState = drivingDisplayStateRef.current;
    const ids = new Set<string>([...displayState.keys(), ...Object.keys(isDrivingMap)]);

    for (const id of ids) {
      const rawValue = isDrivingMap[id];
      if (rawValue == null) {
        displayState.delete(id);
        continue;
      }

      const entry = displayState.get(id);
      if (!entry) {
        displayState.set(id, { value: rawValue });
        continue;
      }

      if (entry.value === rawValue) {
        entry.pendingValue = undefined;
        entry.pendingSince = undefined;
        continue;
      }

      if (entry.pendingValue !== rawValue) {
        entry.pendingValue = rawValue;
        entry.pendingSince = now;
        continue;
      }

      if (entry.pendingSince != null && now - entry.pendingSince >= DRIVING_STATE_SWITCH_DELAY_MS) {
        entry.value = rawValue;
        entry.pendingValue = undefined;
        entry.pendingSince = undefined;
      }
    }

    const nextDisplayMap: Record<string, boolean> = {};
    for (const [id, entry] of displayState.entries()) {
      nextDisplayMap[id] = entry.value;
    }
    setDisplayDrivingMap(nextDisplayMap);
  }, [isDrivingMap]);

  // ─── Follow selected device ─────────────────────────────────────
  // When a device is selected, keep the map centered on it. North stays up: the owner wants the
  // map never to turn with the vehicle.
  useEffect(() => {
    const deviceId = followingDeviceRef.current;
    if (!deviceId) return;
    const pos = animatedPositionMap.get(deviceId);
    if (!pos) return;
    setViewState(prev => {
      const deltaLng = pos[0] - prev.longitude;
      const deltaLat = pos[1] - prev.latitude;

      if (
        Math.abs(deltaLng) < FOLLOW_POSITION_EPSILON &&
        Math.abs(deltaLat) < FOLLOW_POSITION_EPSILON
      ) {
        return prev;
      }

      return {
        ...prev,
        longitude: prev.longitude + deltaLng * FOLLOW_POSITION_LERP_FACTOR,
        latitude: prev.latitude + deltaLat * FOLLOW_POSITION_LERP_FACTOR,
        transitionDuration: undefined,
        transitionInterpolator: undefined,
      };
    });
  }, [animatedPositionMap]);

  // ─── Lazy device name cache for tooltips ─────────────────────────
  const queryClient = useQueryClient();
  // The names already loaded for the labels; only a vehicle not among them is fetched on hover.
  const deviceNamesRef = useRef(deviceNamesById);
  deviceNamesRef.current = deviceNamesById;
  const deviceNameCache = useRef(new globalThis.Map<string, string>());
  const pendingNameFetches = useRef(new Set<string>());
  // Counter to trigger re-render when a name loads
  const [, setNameCacheVersion] = useState(0);

  const fetchDeviceName = useCallback(
    (deviceId: string) => {
      if (deviceNameCache.current.has(deviceId) || pendingNameFetches.current.has(deviceId)) return;
      pendingNameFetches.current.add(deviceId);

      queryClient
        .fetchQuery({
          queryKey: ["device", activeSessionId, deviceId],
          queryFn: ({ signal }) =>
            geotabClient
              .get("Device", { id: deviceId }, 1, signal)
              .then(devices => devices[0] ?? null),
          staleTime: 5 * 60_000,
        })
        .then(device => {
          if (device?.name) {
            deviceNameCache.current.set(deviceId, device.name);
            // Trigger re-render so tooltip updates from skeleton → name
            setNameCacheVersion(c => c + 1);
          }
        })
        .finally(() => {
          pendingNameFetches.current.delete(deviceId);
        });
    },
    [queryClient, activeSessionId, geotabClient],
  );

  // ─── Fixed tooltip state ────────────────────────────────────────
  const [tooltipInfo, setTooltipInfo] = useState<TooltipInfo | null>(null);

  const handleHover = useCallback(
    ({ object, x, y }: { object?: ClusterOrPoint; x: number; y: number }) => {
      if (!object) {
        setTooltipInfo(null);
        return;
      }
      if (isCluster(object)) {
        setTooltipInfo({
          x,
          y,
          isCluster: true,
          clusterCount: object.properties.point_count,
        });
        return;
      }
      const deviceId = object.properties.deviceId;
      if (!deviceNamesRef.current[deviceId] && !deviceNameCache.current.has(deviceId)) {
        fetchDeviceName(deviceId);
      }
      setTooltipInfo({ x, y, isCluster: false, deviceId });
    },
    [fetchDeviceName],
  );

  const tooltipText = (() => {
    if (!tooltipInfo) return null;

    if (tooltipInfo.isCluster) {
      return t("map.vehicles", { count: tooltipInfo.clusterCount ?? 0 });
    }

    const deviceId = tooltipInfo.deviceId;
    if (!deviceId) return null;

    const deviceName = deviceNamesById[deviceId] ?? deviceNameCache.current.get(deviceId);
    return deviceName ?? null;
  })();

  const { clusterData, pointData } = useMemo(() => {
    const cd: ClusterOrPoint[] = [];
    const pd: ClusterOrPoint[] = [];
    for (const c of clusters) {
      if (isCluster(c)) {
        cd.push(c);
      } else {
        pd.push(c);
      }
    }
    return { clusterData: cd, pointData: pd };
  }, [clusters]);

  const labelPoints = useMemo(
    () =>
      pointData.map(point => ({
        deviceId: point.properties.deviceId,
        coordinates: point.geometry.coordinates as [number, number],
      })),
    [pointData],
  );

  // Cluster bubbles carry no name of their own but hide their count when a name lands on them.
  const labelObstacles = useMemo(
    () =>
      clusterData.map(cluster => ({
        coordinates: cluster.geometry.coordinates as [number, number],
        halfSizePx: clusterIconSize(isCluster(cluster) ? cluster.properties.point_count : 1) / 2,
      })),
    [clusterData],
  );

  // What the map knows about each visible vehicle. The labels and the vehicle list both read this,
  // so the facts behind them cannot drift apart.
  const fuelCellDeviceIds = useFuelCellDeviceIds();

  const assetRows = useMapAssetRows({
    deviceIds: stableVisibleIds,
    names: deviceNamesById,
    statusInfoMap,
    statusInfoMapUpdatedAt,
    drivingById: displayDrivingMap,
    fuelCellDeviceIds,
  });

  // The last positions actually reported, not the smoothed ones: these feed the address lookup,
  // whose query key would otherwise change sixty times a second.
  const positionById = useMemo(() => toPositionMap(stableCoords), [stableCoords]);

  /**
   * What each name label carries besides the name.
   *
   * The drive-type word is only put in when the reader asked for it, so the layout does not
   * reserve room for something that is not drawn.
   */
  const labelBadgeById = useMemo(() => {
    const badges = new globalThis.Map<string, AssetLabelBadge>();
    for (const row of assetRows) {
      badges.set(row.deviceId, {
        driveType: row.driveType,
        energyPercent: row.energyPercent,
        isCharging: row.isCharging,
        powertrainLabel: settings.showPowertrainBadges
          ? POWERTRAIN_LABELS[row.driveType]
          : undefined,
      });
    }
    return badges;
  }, [assetRows, settings.showPowertrainBadges]);

  const assetLabels = useAssetLabels({
    enabled: settings.showAssetNames,
    points: labelPoints,
    obstacles: labelObstacles,
    badgeById: labelBadgeById,
    animatedPositions: animatedPositionMap,
    names: deviceNamesById,
    viewState,
    containerSize,
    iconSizePx: settings.vehicleIconSizePx,
  });

  // ─── Layers ──────────────────────────────────────────────────────

  /**
   * How a vehicle is drawn: its group's own symbol if that group has one, otherwise the built-in
   * arrow or square in the group's colour.
   *
   * An uploaded symbol is drawn as its author drew it. Recolouring arbitrary artwork to one colour
   * turns a two-colour logo into a silhouette, and the person who uploaded it chose how it looks.
   */
  /**
   * The colour and symbol each vehicle is drawn in, worked out once.
   *
   * Four things need this — the marker, its heading arrow, the ring around it and the row in the
   * list — and each used to search the group styles for itself, some of them twice over. That is
   * a linear search per vehicle per draw, for an answer that only changes when a group's colour
   * does.
   */
  const styleByDevice = useMemo(() => {
    const styles = new globalThis.Map<string, { color: string; svg?: string }>();
    for (const [deviceId, groupIds] of groupIdsByDevice) {
      const style = groupStyleOf(groupIds, groupStyles.groups);
      styles.set(deviceId, {
        color: style?.color || settings.vehicleIconColor || primaryColor,
        svg: style?.svg,
      });
    }
    return styles;
  }, [groupIdsByDevice, groupStyles.groups, settings.vehicleIconColor, primaryColor]);

  const fallbackColor = settings.vehicleIconColor || primaryColor;

  /**
   * The layer colours, as deck.gl wants them.
   *
   * Each of these parses a hex string and allocates an array. Worked out inside the layers, that
   * happened on every render — sixty times a second while anything is moving — for colours that
   * only change when the reader touches a slider.
   */
  const layerColors = useMemo(
    () => ({
      zoneFill: hexToRgba(primaryColor, 8, primaryColor),
      zoneLine: hexToRgba(primaryColor, 45, primaryColor),
      ringFill: hexToRgba(settings.ringFillColor, settings.ringFillOpacity, "#ffffff"),
      ringBorder: hexToRgba(settings.ringBorderColor, settings.ringBorderOpacity, primaryColor),
    }),
    [
      primaryColor,
      settings.ringFillColor,
      settings.ringFillOpacity,
      settings.ringBorderColor,
      settings.ringBorderOpacity,
    ],
  );

  const colorOfDevice = useCallback(
    (deviceId: string) => styleByDevice.get(deviceId)?.color ?? fallbackColor,
    [styleByDevice, fallbackColor],
  );

  /** The heading a vehicle last reported, falling back to the last one seen. */
  const bearingOfDevice = useCallback(
    (deviceId: string) => {
      const latest = statusInfoMap[deviceId]?.bearing;
      return typeof latest === "number" && Number.isFinite(latest)
        ? normalizeBearing(latest)
        : (lastBearingByDeviceRef.current.get(deviceId) ?? 0);
    },
    [statusInfoMap],
  );

  const symbolOfDevice = useCallback(
    (deviceId: string) => styleByDevice.get(deviceId)?.svg,
    [styleByDevice],
  );

  const iconOfDevice = useCallback(
    (deviceId: string, isDriving: boolean) => {
      const style = styleByDevice.get(deviceId);
      const color = style?.color ?? fallbackColor;

      if (style?.svg) {
        return groupSymbolIcon(style.svg, color);
      }

      const icons = getVehicleIcons(color);
      return isDriving ? icons.driving : icons.stopped;
    },
    [styleByDevice, fallbackColor],
  );

  /**
   * Geotab's zones, under everything else.
   *
   * Faint on purpose: they are context for where the vehicles are, not the subject. Only fetched
   * while they are switched on.
   */
  const zoneLayer = new PolygonLayer({
    id: "zones",
    data: zoneOutlines,
    visible: settings.showZones,
    getPolygon: (zone: ZoneOutline) => zone.points.map(point => [point.x, point.y]),
    stroked: true,
    filled: true,
    lineWidthUnits: "pixels",
    getLineWidth: 1,
    getFillColor: layerColors.zoneFill,
    getLineColor: layerColors.zoneLine,
    pickable: false,
  });

  /**
   * Half the icon, plus whatever distance was asked for. So the ring sits exactly around the
   * symbol at a distance of nothing, grows and shrinks with it, and the heading marker has
   * somewhere definite to sit.
   */
  const ringRadiusPx = Math.max(2, settings.vehicleIconSizePx / 2 + settings.ringPaddingPx);

  /**
   * The ring under the vehicles.
   *
   * Its own layer beneath the icons rather than part of them: the icons are images and a ring
   * whose width, colour and transparency are all adjustable would mean rebuilding an image on
   * every drag of a slider.
   */
  const vehicleRingLayer = new ScatterplotLayer({
    id: "vehicle-rings",
    data: pointData,
    getPosition: (d: ClusterOrPoint) =>
      animatedPositionMap.get(d.properties.deviceId) ??
      (d.geometry.coordinates as [number, number]),
    radiusUnits: "pixels",
    getRadius: ringRadiusPx,
    stroked: true,
    filled: true,
    lineWidthUnits: "pixels",
    getLineWidth: settings.ringBorderWidthPx,
    getFillColor: layerColors.ringFill,
    getLineColor: layerColors.ringBorder,
    updateTriggers: {
      getPosition: [animatedPositionMap],
      getRadius: [settings.vehicleIconSizePx, settings.ringPaddingPx],
      getLineWidth: [settings.ringBorderWidthPx],
      getFillColor: [settings.ringFillColor, settings.ringFillOpacity],
      getLineColor: [settings.ringBorderColor, settings.ringBorderOpacity, primaryColor],
    },
  });

  const myLocationColor = theme.palette.info.main;
  const myLocationData = deviceLocation ? [deviceLocation] : [];

  /** How far off the browser may be, drawn in metres so it grows and shrinks with the zoom. */
  const myLocationAccuracyLayer = new ScatterplotLayer({
    id: "my-location-accuracy",
    data: myLocationData,
    getPosition: (d: DeviceLocation) => [d.longitude, d.latitude],
    radiusUnits: "meters",
    getRadius: (d: DeviceLocation) => d.accuracyM,
    getFillColor: hexToRgba(myLocationColor, 15, myLocationColor),
    stroked: true,
    lineWidthUnits: "pixels",
    getLineWidth: 1,
    getLineColor: hexToRgba(myLocationColor, 40, myLocationColor),
    updateTriggers: {
      getFillColor: [myLocationColor],
      getLineColor: [myLocationColor],
    },
  });

  const myLocationDotLayer = new ScatterplotLayer({
    id: "my-location-dot",
    data: myLocationData,
    getPosition: (d: DeviceLocation) => [d.longitude, d.latitude],
    radiusUnits: "pixels",
    getRadius: 7,
    getFillColor: hexToRgba(myLocationColor, 100, myLocationColor),
    stroked: true,
    lineWidthUnits: "pixels",
    getLineWidth: 2,
    getLineColor: [255, 255, 255, 255],
    updateTriggers: {
      getFillColor: [myLocationColor],
    },
  });

  const vehicleIconLayer = new IconLayer({
    id: "vehicle-icons",
    data: pointData,
    getPosition: (d: ClusterOrPoint) =>
      animatedPositionMap.get(d.properties.deviceId) ??
      (d.geometry.coordinates as [number, number]),
    getSize: settings.vehicleIconSizePx,
    sizeUnits: "pixels",
    getAngle: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving = displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      // A group's own symbol stays upright; its heading is shown by the marker on the ring.
      if (!isDriving || symbolOfDevice(deviceId)) return 0;

      return vehicleIconAngle(bearingOfDevice(deviceId), viewState.bearing);
    },
    pickable: true,
    getIcon: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving =
        displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      return iconOfDevice(deviceId, isDriving);
    },
    onClick: ({ object }: { object?: ClusterOrPoint }) => {
      if (object && !isCluster(object)) {
        const deviceId = object.properties.deviceId;
        setSelectedDeviceId(deviceId);
        followingDeviceRef.current = deviceId;
        const pos = animatedPositionMap.get(deviceId);
        if (pos) {
          flyTo(pos[0], pos[1], Math.max(viewState.zoom, 16), 2000);
        }
      }
    },
    onHover: handleHover,
    updateTriggers: {
      getPosition: [animatedPositionMap],
      getIcon: [iconOfDevice, isDrivingMap, displayDrivingMap],
      getAngle: [statusInfoMap, isDrivingMap, displayDrivingMap, viewState.bearing],
      // Without this the icons keep whatever size they were first drawn at, and the size slider
      // appears to move only the ring behind them.
      getSize: [settings.vehicleIconSizePx],
    },
  });

  /**
   * The heading marker, for vehicles drawn with their group's own symbol.
   *
   * Those symbols do not turn, so the direction goes on the ring around them: the marker is
   * pushed out to the ring's edge along the heading and rotated to point that way. Vehicles that
   * are standing, and vehicles drawn with the built-in arrow, get a marker of no size — that is
   * cheaper than filtering the data, which would rebuild the layer on every change of driving
   * state.
   */
  const headingMarkerLayer = new IconLayer({
    id: "vehicle-heading-markers",
    data: pointData,
    getPosition: (d: ClusterOrPoint) =>
      animatedPositionMap.get(d.properties.deviceId) ??
      (d.geometry.coordinates as [number, number]),
    getSize: (d: ClusterOrPoint) => {
      const deviceId = d.properties.deviceId;
      const isDriving =
        displayDrivingMap[deviceId] ?? isDrivingMap[deviceId] ?? d.properties.isDriving;
      return isDriving && symbolOfDevice(deviceId) ? HEADING_MARKER_SIZE_PX : 0;
    },
    sizeUnits: "pixels",
    getIcon: (d: ClusterOrPoint) => getHeadingMarker(colorOfDevice(d.properties.deviceId)),
    getAngle: (d: ClusterOrPoint) =>
      vehicleIconAngle(bearingOfDevice(d.properties.deviceId), viewState.bearing),
    getPixelOffset: (d: ClusterOrPoint) => {
      // Clockwise from straight up on screen, which is where the marker has to sit.
      const screenAngle =
        ((bearingOfDevice(d.properties.deviceId) - viewState.bearing) * Math.PI) / 180;
      const distance = ringRadiusPx + HEADING_MARKER_SIZE_PX / 2;
      // The y axis of a pixel offset points down, hence the minus.
      return [Math.sin(screenAngle) * distance, -Math.cos(screenAngle) * distance];
    },
    updateTriggers: {
      getPosition: [animatedPositionMap],
      getSize: [isDrivingMap, displayDrivingMap, symbolOfDevice],
      getIcon: [colorOfDevice],
      getAngle: [statusInfoMap, viewState.bearing],
      getPixelOffset: [statusInfoMap, viewState.bearing, ringRadiusPx],
    },
  });

  const clusterIconLayer = new IconLayer({
    id: "cluster-icons",
    data: clusterData,
    getPosition: (d: ClusterOrPoint) => d.geometry.coordinates as [number, number],
    getSize: (d: ClusterOrPoint) =>
      (clusterIconSize(isCluster(d) ? d.properties.point_count : 1) *
        settings.clusterIconScalePercent) /
      100,
    sizeUnits: "pixels",
    pickable: true,
    getIcon: (d: ClusterOrPoint) => {
      const count = isCluster(d) ? d.properties.point_count : 1;
      return {
        url: getClusterIcon(count, clusterColor),
        width: 128,
        height: 128,
      };
    },
    onClick: ({ object }: { object?: ClusterOrPoint }) => {
      if (object && isCluster(object)) {
        const clusterId = object.properties.cluster_id;
        const expansionZoom = getClusterExpansionZoom(clusterId);
        const [lng, lat] = object.geometry.coordinates;
        flyTo(lng, lat, Math.min(20, Math.max(viewState.zoom + 1, expansionZoom)), 500);
      }
    },
    onHover: handleHover,
    updateTriggers: {
      getIcon: [clusterData, clusterColor],
      getSize: [clusterData, settings.clusterIconScalePercent],
    },
  });

  // ─── Search handlers ─────────────────────────────────────────────

  const handleDeviceSelect = useCallback(
    (deviceId: string, lng: number, lat: number) => {
      setSelectedDeviceId(deviceId);
      followingDeviceRef.current = deviceId;
      flyTo(lng, lat, 16);
    },
    [flyTo],
  );

  const handleLocationSelect = useCallback(
    (lng: number, lat: number) => {
      setSelectedDeviceId(null);
      followingDeviceRef.current = null;
      flyTo(lng, lat, 14);
    },
    [flyTo],
  );

  const handleListSelect = useCallback(
    (deviceId: string) => {
      const position = positionById.get(deviceId);
      if (!position) return;
      handleDeviceSelect(deviceId, position[0], position[1]);
    },
    [positionById, handleDeviceSelect],
  );

  const handleCloseVehicleInfo = useCallback(() => {
    setSelectedDeviceId(null);
    followingDeviceRef.current = null;
  }, []);

  /**
   * Frames the whole fleet.
   *
   * Every vehicle whose position we hold, which is exactly the set the group filter in the header
   * allows — so what the reader is filtered to is what gets framed.
   */
  const handleFitFleet = useCallback(() => {
    const bounds = boundsOfPositions(positionById.values());
    if (!bounds) {
      return;
    }

    followingDeviceRef.current = null;
    setViewState(previous => ({
      ...previous,
      ...viewportForBounds(bounds, containerSize.width, containerSize.height),
      transitionDuration: 800,
      transitionInterpolator: new FlyToInterpolator(),
    }));
  }, [positionById, containerSize.width, containerSize.height]);

  // Stop following when user manually pans/zooms
  const handleViewStateChangeWithFollow = useCallback(
    ({ viewState: vs, interactionState }: ViewStateChange) => {
      // If user is actively interacting (not a programmatic fly-to), stop following
      if (interactionState.isPanning || interactionState.isZooming) {
        followingDeviceRef.current = null;
      }
      // deck.gl reports the next view state with optional pitch/bearing; the map
      // controller always supplies both, so fall back to the current values.
      if (!("longitude" in vs)) return;
      setViewState(prev => ({
        ...vs,
        pitch: vs.pitch ?? prev.pitch,
        bearing: vs.bearing ?? prev.bearing,
      }));
    },
    [],
  );

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", position: "relative" }}>
      <DeckGL
        deviceProps={{ type: "webgl" }}
        viewState={viewState}
        onViewStateChange={handleViewStateChangeWithFollow}
        // North always up: no turning by dragging, two fingers or the keyboard.
        controller={{ dragRotate: false, touchRotate: false, keyboard: { rotateSpeedX: 0 } }}
        layers={[
          zoneLayer,
          myLocationAccuracyLayer,
          vehicleRingLayer,
          vehicleIconLayer,
          headingMarkerLayer,
          clusterIconLayer,
          myLocationDotLayer,
        ]}
        style={{ width: "100%", height: "100%" }}
      >
        <Map mapStyle={MAP_STYLES[settings.mapStyle]}>
          {/*
            Layers of their own rather than part of the chosen basemap, so the traffic can be
            shown over any of them — including the plain OpenStreetMap one, which is what a
            dispatcher watching a jam actually wants under it.
          */}
          {trafficLayers.map(({ key, tiles }) => (
            <Source key={key} id={`traffic-${key}`} type="raster" tiles={tiles} tileSize={256}>
              <Layer id={`traffic-${key}`} type="raster" paint={{ "raster-opacity": 0.75 }} />
            </Source>
          ))}
        </Map>
      </DeckGL>

      <AssetLabelOverlay
        labels={assetLabels}
        badgeById={labelBadgeById}
        color={settings.assetNameColor || themeColors.labelText}
        backgroundColor={settings.assetNameBackgroundColor || themeColors.labelBackground}
      />

      <FixedTooltip info={tooltipInfo} text={tooltipText} />

      <MapControlStack>
        <MapToggleButton
          label={
            settings.showAssetNames
              ? t("map.hideAssetNames")
              : t("map.showAssetNames")
          }
          active={settings.showAssetNames}
          icon={
            <LabelIcon
              fontSize="small"
              color={settings.showAssetNames ? "primary" : "disabled"}
            />
          }
          onClick={() => updateSettings({ showAssetNames: !settings.showAssetNames })}
        />
        <MapToggleButton
          label={t("map.fitFleet")}
          icon={<ZoomOutMapIcon fontSize="small" color="action" />}
          onClick={handleFitFleet}
        />
        <MapToggleButton
          label={deviceLocation ? t("map.myLocation.hide") : t("map.myLocation.show")}
          active={deviceLocation != null}
          icon={
            isLocating ? (
              <CircularProgress size={18} />
            ) : (
              <MyLocationIcon fontSize="small" color={deviceLocation ? "primary" : "action"} />
            )
          }
          onClick={() => {
            if (isLocating) return;
            if (deviceLocation) clearDeviceLocation();
            else locate();
          }}
        />
        <MapToggleButton
          label={t("map.settings.title")}
          active={settingsOpen}
          icon={<SettingsIcon fontSize="small" color={settingsOpen ? "primary" : "disabled"} />}
          onClick={() => setSettingsOpen(open => !open)}
        />
      </MapControlStack>

      {settingsOpen ? (
        <MapSettingsPanel
          settings={settings}
          update={updateSettings}
          themeColors={themeColors}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      <MapSearchBar
        devices={devicesById}
        devicePositions={animatedPositionMap}
        onDeviceSelect={handleDeviceSelect}
        onLocationSelect={handleLocationSelect}
      />

      <VehicleListPanel
        rows={assetRows}
        positionById={positionById}
        groupIdsByDevice={groupIdsByDevice}
        colorOfDevice={colorOfDevice}
        showZones={settings.showZones}
        containerWidth={containerSize.width}
        selectedDeviceId={selectedDeviceId}
        onSelect={handleListSelect}
      />

      {selectedDeviceId && (
        <VehicleInfoPanel
          deviceId={selectedDeviceId}
          statusInfoMap={statusInfoMap}
          statusInfoMapUpdatedAt={statusInfoMapUpdatedAt}
          isDrivingMap={displayDrivingMap}
          onClose={handleCloseVehicleInfo}
        />
      )}
    </div>
  );
};

export default LiveMapView;
